import { useSyncExternalStore } from 'react';

import { storage } from '@portal/pkg/storage/local';

const StorageKey = 'docs-language';

/**
 *	The language the reader last picked, shared by every code block on the page
 *	and kept across pages.
 *
 *	An external store rather than state in each block: the blocks are separate
 *	radio groups, so picking Go in one has to move the others, and the answer
 *	has to survive the navigation to the next page. `localStorage` is the whole
 *	persistence layer; the subscriber list is what makes the blocks agree
 *	within one page.
 */
const subscribers = new Set<() => void>();

let current: string = storage.get(StorageKey, '');

function subscribe(notify: () => void): () => void {
	subscribers.add(notify);

	return () => {
		subscribers.delete(notify);
	};
}

export function rememberLanguage(language: string): void {
	if (language === current) {
		return;
	}

	current = language;
	storage.set(StorageKey, language);

	for (const notify of subscribers) {
		notify();
	}
}

/**
 *	The remembered language, or the fallback when there is none yet or the block
 *	does not offer it — a page showing only curl must not leave every tab off.
 */
export function useLanguage(offered: string[]): string {
	const remembered = useSyncExternalStore(
		subscribe,
		() => current,
		() => ''
	);

	return offered.includes(remembered) ? remembered : (offered[0] ?? '');
}
